import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import SectionHeader from "@/components/feature/SectionHeader";
import { animeList, animeCategories } from "@/mocks/anime";
import { articleList } from "@/mocks/articles";
import { faqList } from "@/mocks/faqs";

const stats = [
  { value: "200+", label: "精选短番收录" },
  { value: "12万+", label: "追番同好" },
  { value: "4.9", label: "同好评分" },
  { value: "30+", label: "年度盘点文章" },
];

export default function Home() {
  const hotAnime = animeList.filter((a) => a.isHot);
  const newAnime = animeList.filter((a) => a.isNew);
  const featured = [newAnime[0], hotAnime[1], animeList[5]].filter(Boolean);
  const articles = articleList.slice(0, 3);
  const faqs = faqList.slice(0, 3);

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative h-[560px] w-full overflow-hidden md:h-[620px]">
          <img
            src="https://readdy.ai/api/search-image?query=cozy%20anime%20style%20illustration%20of%20a%20young%20woman%20curled%20up%20on%20a%20soft%20sofa%20watching%20short%20comedy%20anime%20with%20bowl%20of%20instant%20noodles%20and%20popcorn%20beside%20her%2C%20warm%20coral%20orange%20and%20cream%20color%20palette%2C%20cute%20flat%20illustration%2C%20soft%20round%20shapes%2C%20youthful%20cheerful%20mood%2C%20clean%20minimal%20composition&width=1600&height=900&seq=home-hero-2026-01&orientation=landscape"
            alt="小水看番精选泡面番与日常搞笑下饭番"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-foreground-950/40 to-background-950/70" />
          <div className="absolute inset-0 flex w-full items-center">
            <div className="mx-auto w-full max-w-7xl px-4 md:px-6 text-center">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/20 px-4 py-1.5 text-sm text-background-50 backdrop-blur">
                <i className="ri-restaurant-2-line" aria-hidden="true" />
                泡面番 · 三分钟短番 · 下饭番
              </span>
              <h1 className="mt-5 font-heading text-4xl leading-tight text-background-50 md:text-6xl">
                小水看番
              </h1>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-background-100/90 md:text-lg">
                精选日常搞笑短篇，泡面番一口气看完，下饭番轻松刷完不占时间。忙里偷闲，用几分钟收获一整天的快乐。
              </p>
              <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link
                  to="/library"
                  className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
                >
                  <i className="ri-movie-2-line" aria-hidden="true" />
                  去番剧库挑一部
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-7 py-3 text-sm font-medium text-background-50 whitespace-nowrap backdrop-blur transition-colors hover:bg-background-50/25"
                >
                  了解小水看番
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <SectionHeader
            eyebrow="怎么挑"
            title="按你的口味选一部"
            desc="从泡面番到三分钟短番，从日常搞笑到下饭治愈，总能找到一部对得上胃口的宝藏短番。"
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {animeCategories.slice(1).map((cat, i) => (
              <Link
                key={cat.key}
                to={`/library?cat=${encodeURIComponent(cat.key)}`}
                className="group flex flex-col items-center gap-3 rounded-2xl border border-background-200/70 bg-background-100 p-5 text-center transition-all hover:-translate-y-1 hover:border-accent-200"
              >
                <span
                  className={`flex h-14 w-14 items-center justify-center rounded-2xl text-2xl transition-transform group-hover:scale-110 ${
                    i % 3 === 0
                      ? "bg-primary-100 text-primary-700"
                      : i % 3 === 1
                        ? "bg-accent-100 text-accent-700"
                        : "bg-secondary-100 text-secondary-700"
                  }`}
                >
                  <i className={cat.icon} aria-hidden="true" />
                </span>
                <span className="text-sm font-medium text-foreground-900 whitespace-nowrap">{cat.label}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* Hot anime */}
        <section className="bg-background-100/70">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-20">
            <SectionHeader
              eyebrow="人气精选"
              title="同好都在追的爆款短番"
              desc="高口碑、高人气，最受追番同好欢迎的泡面番和下饭番，随时打开随时快乐。"
              moreTo="/library"
            />
            <div className="grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4">
              {hotAnime.map((anime) => (
                <AnimeCard key={anime.id} anime={anime} />
              ))}
            </div>
          </div>
        </section>

        {/* New spotlights */}
        <section className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <SectionHeader
            eyebrow="编辑推荐"
            title="本周值得一看的短番"
            desc="编辑部每周精选，从萌系治愈到沙雕玩梗，总有一部能戳中你的笑点。"
          />
          <div className="grid gap-4 md:gap-6 lg:grid-cols-3">
            {featured.map((anime, i) => (
              <Link
                key={anime.id}
                to={`/anime/${anime.id}`}
                className="group relative flex flex-col justify-end overflow-hidden rounded-2xl"
              >
                <img
                  src={anime.cover}
                  alt={`${anime.name} 封面`}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent" />
                <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
                  <i className="ri-sparkling-2-line" aria-hidden="true" />
                  {i === 0 ? "新番" : i === 1 ? "人气" : "治愈"}
                </span>
                <div className="relative p-5 md:p-6">
                  <h3 className="font-heading text-xl text-background-50 md:text-2xl">{anime.name}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-background-100/85">{anime.desc}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary-300">
                    立即查看
                    <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Stats / about strip */}
        <section className="bg-accent-900 text-background-50">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-16">
            <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
              {stats.map((s) => (
                <div key={s.label} className="text-center">
                  <p className="font-heading text-3xl md:text-4xl text-accent-300">{s.value}</p>
                  <p className="mt-2 text-sm text-accent-100/80">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Articles */}
        <section className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <SectionHeader
            eyebrow="追番指南"
            title="泡面番深度解读与安利"
            desc="入坑指南、年度盘点、下饭安利和冷门宝藏，帮你把每一部下饭番都看得明明白白。"
          />
          <div className="grid gap-6 md:grid-cols-3">
            {articles.map((art) => (
              <Link
                key={art.id}
                to={`/article/${art.id}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-background-100 transition-all hover:-translate-y-1 hover:border-accent-200"
              >
                <div className="aspect-[16/9] w-full overflow-hidden">
                  <img
                    src={art.image}
                    alt={art.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <span className="w-fit rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900">
                    {art.tag}
                  </span>
                  <h3 className="mt-3 font-heading text-lg leading-snug text-foreground-950 group-hover:text-primary-700 transition-colors">
                    {art.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-5 text-foreground-600">{art.summary}</p>
                  <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                    <span>{art.date}</span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-time-line" aria-hidden="true" />
                      约 {art.readMin} 分钟
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* FAQ preview */}
        <section className="bg-background-100/70">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-20">
            <SectionHeader
              eyebrow="常见问题"
              title="关于泡面番，你想知道的都在这里"
              desc="第一次接触泡面番？看看大家最常问的问题，帮你快速入门下饭短番的世界。"
              moreTo="/faq"
              moreLabel="更多问题"
            />
            <div className="grid gap-4 md:grid-cols-3">
              {faqs.map((f) => (
                <div
                  key={f.q}
                  className="rounded-2xl border border-background-200/70 bg-background-50 p-5"
                >
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className="ri-question-line" aria-hidden="true" />
                  </span>
                  <h3 className="mt-3 font-heading text-base text-foreground-950">{f.q}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <div className="relative overflow-hidden rounded-3xl bg-primary-500 px-6 py-12 text-center md:py-16">
            <div className="absolute -right-6 -top-6 h-40 w-40 rounded-full bg-background-50/10" />
            <div className="absolute -bottom-10 -left-6 h-48 w-48 rounded-full bg-background-50/10" />
            <div className="relative">
              <h2 className="font-heading text-3xl text-background-50 md:text-4xl">
                今天摸鱼，也要有点乐子
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-base text-background-50/90">
                去番剧库挑一部泡面番，五分钟就能收获一整天的好心情，轻松刷完不占时间。
              </p>
              <Link
                to="/library"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-background-50 px-7 py-3 text-sm font-medium text-primary-800 whitespace-nowrap transition-colors hover:bg-background-100"
              >
                现在就去挑一部
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}